import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  App as AntApp,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";

import { returnsApi } from "../api/returns";
import type { ReturnItem, ReturnRequest, ReturnStatus } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { formatDateTimeIST, formatINR, formatRelative } from "../utils/format";

const STATUS_TABS: { label: string; value: ReturnStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

export function Returns() {
  const { notification, modal } = AntApp.useApp();
  const [status, setStatus] = useState<ReturnStatus | "all">("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState<ReturnRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState<ReturnRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const res = await returnsApi.list(params);
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (row: ReturnRequest) => {
    setReviewing(row);
    setRejectionReason("");
  };

  const submitReview = async (
    decision: "approve" | "reject",
    overrideCredit = false,
  ) => {
    if (!reviewing) return;
    if (decision === "reject" && !rejectionReason.trim()) {
      notification.warning({ message: "Reason is required to reject" });
      return;
    }
    try {
      await returnsApi.review(reviewing.id, {
        decision,
        rejection_reason: decision === "reject" ? rejectionReason : undefined,
        override_credit: overrideCredit || undefined,
      });
      notification.success({
        message: `${reviewing.return_number} ${
          decision === "approve" ? "approved" : "rejected"
        }`,
      });
      setReviewing(null);
      load();
    } catch (e: unknown) {
      const err = e as {
        response?: {
          data?: {
            detail?: string;
            code?: string;
            items?: { product: string; available: number; requested: number }[];
          };
        };
      };
      const data = err.response?.data;
      if (decision === "approve" && data?.code === "credit_limit_exceeded") {
        modal.confirm({
          title: "Credit limit exceeded",
          content: `${data.detail} Approve anyway and override the credit limit?`,
          okText: "Override & Approve",
          okButtonProps: { danger: true },
          onOk: () => submitReview("approve", true),
        });
        return;
      }
      if (data?.code === "insufficient_stock") {
        modal.error({
          title: "Not enough stock for the replacement",
          content: (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {(data.items ?? []).map((i) => (
                <li key={i.product}>
                  {i.product}: {i.available} available, {i.requested} needed
                </li>
              ))}
            </ul>
          ),
        });
        return;
      }
      notification.error({ message: data?.detail || "Review failed" });
    }
  };

  const columns = [
    {
      title: "Return #",
      dataIndex: "return_number",
      render: (v: string) => (
        <span className="mono" style={{ fontSize: 12 }}>
          {v}
        </span>
      ),
    },
    {
      title: "Order",
      dataIndex: "order_number",
      render: (v: string) => (
        <span className="mono" style={{ fontSize: 12 }}>
          {v}
        </span>
      ),
    },
    { title: "Shop", dataIndex: "shop_name" },
    { title: "Raised by", dataIndex: "raised_by_name" },
    {
      title: "Type",
      dataIndex: "kind",
      render: (v: string) => (
        <Tag color={v === "refund" ? "gold" : "blue"}>{v}</Tag>
      ),
    },
    {
      title: "Net",
      dataIndex: "net_amount",
      align: "right" as const,
      render: (v: string) => <NetAmount value={v} />,
    },
    {
      title: "Raised",
      dataIndex: "submitted_at",
      render: (v: string) => (
        <Tooltip title={formatDateTimeIST(v)}>
          <span className="small-muted">{formatRelative(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: ReturnStatus) => <Pill kind={v} />,
    },
    {
      title: "",
      render: (_: unknown, row: ReturnRequest) => (
        <Button type="primary" size="small" onClick={() => openReview(row)}>
          {row.status === "pending" ? "Review" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <PageHeader
        title="Replacements & Refunds"
        subtitle="Review returns raised against accepted orders"
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Radio.Group
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          optionType="button"
        >
          {STATUS_TABS.map((t) => (
            <Radio.Button key={t.value} value={t.value}>
              {t.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Space>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Return #, order #, shop"
            style={{ width: 260 }}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </div>

      <div className="card">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </div>

      <Modal
        open={!!reviewing}
        onCancel={() => setReviewing(null)}
        width={760}
        title={
          reviewing && (
            <span>
              {reviewing.kind === "refund" ? "Refund" : "Replacement"}{" "}
              <span className="mono">{reviewing.return_number}</span>
            </span>
          )
        }
        footer={
          reviewing?.status === "pending"
            ? [
                <Button key="cancel" onClick={() => setReviewing(null)}>
                  Cancel
                </Button>,
                <Button
                  key="reject"
                  danger
                  onClick={() => submitReview("reject")}
                >
                  Reject
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  onClick={() => submitReview("approve")}
                >
                  Approve
                </Button>,
              ]
            : [
                <Button key="close" onClick={() => setReviewing(null)}>
                  Close
                </Button>,
              ]
        }
      >
        {reviewing && (
          <div style={{ display: "grid", gap: 16 }}>
            <ReviewSummary req={reviewing} />

            <div>
              <div className="kpi-label" style={{ marginBottom: 8 }}>
                Items
              </div>
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={reviewing.items}
                columns={[
                  {
                    title: "Returned",
                    render: (_: unknown, row: ReturnItem) => (
                      <>
                        {row.product_name}
                        {row.product_colour && (
                          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {row.product_colour}
                          </div>
                        )}
                      </>
                    ),
                  },
                  { title: "Qty", dataIndex: "quantity", align: "right" as const },
                  {
                    title: "Value",
                    dataIndex: "subtotal",
                    align: "right" as const,
                    render: (v: string) => (
                      <span className="tabular">{formatINR(v)}</span>
                    ),
                  },
                  {
                    title: "Replacement",
                    render: (_: unknown, row: ReturnItem) =>
                      row.replacement_product_name ? (
                        <>
                          {row.replacement_product_name}
                          {row.replacement_colour && (
                            <div
                              style={{ fontSize: 12, color: "var(--text-secondary)" }}
                            >
                              {row.replacement_colour}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {row.replacement_quantity} ×{" "}
                            {formatINR(row.replacement_unit_price ?? 0)}
                          </div>
                        </>
                      ) : (
                        <span className="small-muted">—</span>
                      ),
                  },
                ]}
              />
              <div
                className="small-muted"
                style={{ marginTop: 8, fontSize: 12 }}
              >
                Returned goods are not added back to sellable stock. Any
                replacement items are deducted on approval.
              </div>
            </div>

            {reviewing.reason && (
              <div>
                <div className="kpi-label">Reason given</div>
                <div style={{ fontSize: 13 }}>{reviewing.reason}</div>
              </div>
            )}

            {reviewing.status === "pending" && (
              <div>
                <div className="kpi-label" style={{ marginBottom: 4 }}>
                  Rejection reason (required to reject)
                </div>
                <Input.TextArea
                  rows={2}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Why is this being rejected?"
                />
              </div>
            )}

            {reviewing.status === "rejected" && reviewing.rejection_reason && (
              <div>
                <div className="kpi-label">Rejected because</div>
                <div style={{ fontSize: 13 }}>{reviewing.rejection_reason}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** Positive net credits the shop; negative means the shop owes more. */
function NetAmount({ value }: { value: string }) {
  const n = Number(value);
  if (n === 0) return <span className="tabular small-muted">—</span>;
  return (
    <span
      className="tabular"
      style={{ color: n > 0 ? "var(--success)" : "var(--text-primary)" }}
    >
      {n > 0 ? "−" : "+"}
      {formatINR(Math.abs(n))}
    </span>
  );
}

function ReviewSummary({ req }: { req: ReturnRequest }) {
  const net = Number(req.net_amount);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 12,
        padding: 12,
        background: "var(--bg-hover)",
        borderRadius: 8,
      }}
    >
      <Metric label="Order" value={req.order_number} mono />
      <Metric label="Shop" value={req.shop_name} />
      <Metric label="Goods returned" value={formatINR(req.refund_value)} />
      <Metric
        label="Goods sent out"
        value={
          Number(req.replacement_value) > 0
            ? formatINR(req.replacement_value)
            : "—"
        }
      />
      <Metric
        label={net >= 0 ? "Credit to shop" : "Shop owes"}
        value={formatINR(Math.abs(net))}
      />
      <Metric
        label={req.status === "approved" ? "Outstanding after" : "Effect"}
        value={
          req.outstanding_after !== null
            ? formatINR(req.outstanding_after)
            : net === 0
              ? "No balance change"
              : net > 0
                ? `Reduces balance by ${formatINR(net)}`
                : `Raises balance by ${formatINR(-net)}`
        }
      />
    </div>
  );
}

function Metric({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className={mono ? "mono" : undefined} style={{ fontSize: 13 }}>
        {value}
      </div>
    </div>
  );
}
